// The SPA's sign-in and invite DECISIONS, as pure functions with no React, Auth0 or DOM imports,
// so scripts/session.selfcheck.ts can pin them (apps/web has no vitest; plan §10). Three review
// rounds found real bugs in exactly these decisions; change them here, with the selfcheck.
import { ACCESS_ERRORS, type AccessError } from "@hyphesync/shared";

/** A non-2xx answer. `code` is the API's `error` field (e.g. "no_access", "last_admin"). */
export class ApiError extends Error {
  constructor(readonly status: number, readonly code: string) {
    super(`${status} ${code}`);
  }
}

/** The session can't be used any more (Auth0 refused to mint a token, or the API answered 401):
 *  only a fresh sign-in fixes it, so it is never shown as a connection problem with a Retry. */
export class SessionExpiredError extends Error {}

const AUTH0_SESSION_ENDED = new Set([
  "login_required", "consent_required", "interaction_required", "invalid_grant", "missing_refresh_token",
]);

/** Does this getAccessTokenSilently() failure mean the Auth0 session has ended? */
export function isAuth0SessionEnded(e: unknown): boolean {
  const code = (e as { error?: unknown } | null)?.error;
  return typeof code === "string" && AUTH0_SESSION_ENDED.has(code);
}

/** What an API response status means before its body is read. */
export function responseOutcome(status: number): "ok" | "no_content" | "session_ended" | "error" {
  if (status === 401) return "session_ended"; // retrying the same refused token can't help
  if (status === 204) return "no_content";
  return status >= 200 && status < 300 ? "ok" : "error";
}

const TOKEN = /^[A-Za-z0-9_-]{43}$/;
export const isInviteToken = (t: string | null | undefined): t is string => typeof t === "string" && TOKEN.test(t);

/** Is this the invite landing path? Accepts /invite and /invite/ (mail scanners and link
 *  rewriters append slashes). The ONE definition: invite.ts uses it to decide whether to rewrite
 *  the URL, so the selfcheck covers the real decision. */
export const isInvitePath = (pathname: string): boolean => pathname.replace(/\/+$/, "") === "/invite";

/** The invite token in a landing URL, or null; the token is the whole fragment. */
export function inviteTokenFromLocation(pathname: string, hash: string): string | null {
  if (!isInvitePath(pathname)) return null;
  const token = hash.startsWith("#") ? hash.slice(1) : hash;
  return isInviteToken(token) ? token : null;
}

const BLOCKED = new Set<string>(ACCESS_ERRORS);
export const isAccessError = (code: string): code is AccessError => BLOCKED.has(code);

/** Refusals that end an invite token's life. Everything else KEEPS it: after email_mismatch or
 *  org_mismatch the screen says "sign out and sign in with the invited account", and that only
 *  works if the token is still waiting when they come back. */
const SPENDS = new Set<AccessError>(["invitation_expired", "invitation_invalid", "already_member", "already_accepted"]);
export const spendsInvite = (code: AccessError): boolean => SPENDS.has(code);

export type Failure =
  | { kind: "expired" }
  | { kind: "blocked"; reason: AccessError }
  | { kind: "error" };

/** How the session gate shows a failed /api/me (or accept). */
export function classifyFailure(e: unknown): Failure {
  if (e instanceof SessionExpiredError) return { kind: "expired" };
  if (e instanceof ApiError && isAccessError(e.code)) return { kind: "blocked", reason: e.code };
  return { kind: "error" };
}

/** Should the gate try the waiting invite after this /api/me failure? */
export const shouldAcceptInvite = (e: unknown, token: string | null): token is string =>
  e instanceof ApiError && e.code === "no_access" && token !== null;
